import { Component } from 'react';
import { Table, TableVariants } from '@clockout-dev/design-elements';
import Inherit from '../../../core/inherit';
import LedgerTableController from './LedgerTable.controller';
import { resolveLedgerRenderZone } from '../../../utils/dates';
import {
    LEDGER_CLEAR_FILTERS_LABEL,
    LEDGER_EMPTY_DESCRIPTION,
    LEDGER_RETRY_LABEL,
    LEDGER_ROW_ACTION,
} from './ledgerTable.constants';
import styles from './styles/ledger-table.module.scss';

/**
 * Ledger transactions table, shared by Reports › Transactions and the User page.
 *
 * Columns, in render order: [user], transaction type (filterable), advance
 * type, amount, fee, created date, processed date, destination account — plus
 * an optional actions column. They and their cell renderers live on
 * {@link LedgerTableController}, so this stays a shell around the design
 * system's `Table`.
 *
 * The two consumers differ in exactly two props: `showUserColumn` (Reports:
 * true, User page: false) and `rowAction` (Reports: view user, User page: open
 * a ticket). Everything else — column order, formatters, tones, empty-state
 * copy — is inside, so the two screens cannot disagree.
 *
 * Sorting and filtering are server-side; the table is paginated by the backend,
 * so neither can be done in the browser.
 *
 * `sort` and `typeFilter` are controlled: whatever the caller passes is what the
 * headers show, so the UI can only ever display the ordering the server actually
 * applied.
 *
 * The caller closes that loop through the URL rather than through `onChange`'s
 * arguments. With `saveSearchParams` the design system's Table writes the new
 * page, sort and filters to the query string BEFORE `onChange` fires — the
 * reset to page 1 on a re-slicing sort or filter included — so a handler reads
 * that back with `getLedgerStateFromUrl` from `utils/ledger`, puts `sort` and
 * `type` into its own state, and re-fetches with `buildLedgerParams` from the
 * same module — plain functions, so nothing has to inherit this table. Ignoring
 * antd's arguments is the point: one writer and one reader means the URL and
 * the fetched slice cannot disagree. `handleTransactionsTableChange` in
 * `users/user/user.controller.js` and in `reports/reports-transactions.controller.js`
 * are the worked examples.
 *
 * Timestamps render in the zone the ledger endpoint declared for the payload
 * (`timezone`), falling back to `LEDGER_TIMEZONE`; the Created / Processed
 * headings name that zone so it is visible without hovering.
 *
 * @param {object} props
 * @param {Array<object>} [props.rows]
 * @param {boolean} [props.loading]
 * @param {{current: number, perPage: number, total?: number} | null} [props.pageInfo]
 * @param {(pagination, filters, sorter, extra) => void} [props.onChange] fired
 *   after the Table has written the new state to the URL; read it back from
 *   there instead of from these arguments
 * @param {{sortBy: string, sortDir: string} | null} [props.sort]
 * @param {string|null} [props.typeFilter] comma-separated ledger types
 * @param {boolean} [props.showTypeFilter=true] the Type column's own dropdown; off when a filter bar owns `type`
 * @param {boolean} [props.showFees] offer service-fee rows in the type filter
 * @param {boolean} [props.showUserColumn=false]
 * @param {(clientId: string) => void} [props.onViewUser]
 * @param {boolean} [props.canViewUser=true]
 * @param {boolean} [props.canAction] renders the actions column
 * @param {string} [props.rowAction] a LEDGER_ROW_ACTION value; defaults to opening a ticket
 * @param {(record: object) => void} [props.onRowAction]
 * @param {string|null} [props.timezone] IANA zone the payload declared; cells and headings render in it
 * @param {boolean} [props.failed=false] the last fetch failed, as opposed to
 *   returning nothing — the two are different facts and get different copy
 * @param {() => void} [props.onRetry] re-fetches the slice the URL describes;
 *   without it the failure state has nothing to offer but a reload
 * @param {boolean} [props.hasActiveFilters=false] picks the "no match" empty copy and shows the clear action
 * @param {() => void} [props.onClearFilters]
 * @param {import('react').ReactNode} [props.emptyDescription] override the built-in empty copy
 * @param {boolean} [props.saveSearchParams=true] let the Table own the query
 *   string. Turning it off breaks the read-back contract above — the handler
 *   would have nothing to read
 * @param {object|false} [props.scroll] antd's scroll config. Defaults to
 *   `{ x: 'max-content' }`: eight columns do not fit a narrow split-layout pane
 *   at tablet width, and the choice is between scrolling the table body and
 *   letting the page itself overflow sideways. Pass `false` to opt out
 * @param {{shell?: string, root?: string}} [props.classNames]
 * @param {object|false} [props.rowSelection] passthrough for export selection
 * @param {Array} [props.selectedRowKeys]
 * @param {Function} [props.onSelectedRowsChange]
 * @extends {LedgerTableController}
 * @extends {Component}
 */
class LedgerTable extends Inherit(LedgerTableController, Component) {
    /**
     * What an empty table says. Three facts, three sentences, in precedence
     * order: a failed fetch (the rows were cleared, so "no transactions" would
     * be false), nothing matching the active filters (with the way out), and a
     * genuinely empty ledger. A caller's `emptyDescription` overrides all three.
     *
     * The failure carries a control rather than an instruction. Telling someone
     * to reload the page asks them to throw away the rest of the page to retry
     * one request, and `onRetry` re-reads the query string, so it lands on
     * exactly the page, sort and filter the headers are showing.
     */
    renderEmptyDescription() {
        const { emptyDescription, failed = false, onRetry, hasActiveFilters = false, onClearFilters } = this.props;
        if (emptyDescription !== undefined) return emptyDescription;
        if (failed) {
            return (
                <span className={styles.emptyState}>
                    <span>{LEDGER_EMPTY_DESCRIPTION.FAILED}</span>
                    {onRetry ? (
                        <button type="button" className={styles.emptyStateAction} onClick={() => onRetry()}>
                            {LEDGER_RETRY_LABEL}
                        </button>
                    ) : null}
                </span>
            );
        }
        if (!hasActiveFilters) return LEDGER_EMPTY_DESCRIPTION.NO_TRANSACTIONS;
        return (
            <span className={styles.emptyState}>
                <span>{LEDGER_EMPTY_DESCRIPTION.NO_MATCH}</span>
                {onClearFilters ? (
                    <button type="button" className={styles.emptyStateAction} onClick={() => onClearFilters()}>
                        {LEDGER_CLEAR_FILTERS_LABEL}
                    </button>
                ) : null}
            </span>
        );
    }

    render() {
        const {
            rows,
            loading,
            pageInfo,
            onChange,
            sort = null,
            typeFilter = null,
            showTypeFilter = true,
            // Off by default: the fee already rides on its advance as a column,
            // and the endpoint hides fee rows unless a type filter asks for
            // them, so offering the choice would show the same money twice.
            showFees = false,
            showUserColumn = false,
            onViewUser,
            canViewUser = true,
            canAction = true,
            rowAction = LEDGER_ROW_ACTION.OPEN_TICKET,
            onRowAction,
            timezone: declaredTimezone = null,
            classNames,
            saveSearchParams = true,
            rowSelection,
            selectedRowKeys,
            onSelectedRowsChange,
        } = this.props;

        const timezone = resolveLedgerRenderZone(declaredTimezone);

        return (
            <Table
                variant={TableVariants.HISTORY}
                columns={this.getLedgerColumns({
                    sort,
                    typeFilter,
                    showTypeFilter,
                    showFees,
                    showUserColumn,
                    onViewUser,
                    canViewUser,
                    canAction,
                    rowAction,
                    onRowAction,
                    timezone,
                })}
                dataSource={rows || []}
                pageInfo={pageInfo || null}
                onChange={onChange}
                loading={loading}
                rowKey="id"
                emptyDescription={this.renderEmptyDescription()}
                classNames={classNames}
                saveSearchParams={saveSearchParams}
                {...(rowSelection !== undefined ? { rowSelection } : {})}
                {...(selectedRowKeys !== undefined ? { selectedRowKeys } : {})}
                {...(onSelectedRowsChange ? { onSelectedRowsChange } : {})}
            />
        );
    }
}

export default LedgerTable;
